/**
 * Date Utility Functions for UTC to Local Timezone Conversion
 * Automatically detects user's timezone and converts UTC timestamps for display
 */

/**
 * Get user's timezone automatically (browser detects based on location)
 * @returns {string} User's IANA timezone (e.g., 'Asia/Kolkata', 'America/New_York')
 */
export function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    // Fallback to UTC if detection fails
    return "UTC";
  }
}

/**
 * Convert UTC timestamp to user's local timezone for display
 * @param {string|Date} utcDate - UTC date string or Date object from backend
 * @param {string} format - Format type: 'date', 'datetime', 'time', 'full'
 * @param {string} timezone - Optional timezone override (defaults to user's timezone)
 * @returns {string} Formatted date string in user's timezone
 */
export function formatUTCDate(utcDate, format = "date", timezone = null) {
  if (!utcDate) return "—";

  try {
    // Parse the UTC date
    let date;
    if (utcDate instanceof Date) {
      date = utcDate;
    } else {
      let dateStr = String(utcDate).trim();

      // Check if string already has timezone info (Z, +/-, GMT, UTC, etc.)
      const hasTimezone =
        dateStr.includes("Z") ||
        dateStr.includes("+") ||
        dateStr.includes("GMT") ||
        dateStr.includes("UTC") ||
        /[+-]\d{2}:\d{2}$/.test(dateStr);

      if (hasTimezone) {
        // Date constructor can handle RFC 2822 format (e.g., "Fri, 09 Jan 2026 07:39:24 GMT")
        date = new Date(dateStr);
      } else {
        // Replace space with T if needed for proper ISO format
        if (dateStr.includes(" ") && !dateStr.includes("T")) {
          dateStr = dateStr.replace(" ", "T");
        }
        // Append 'Z' to indicate UTC
        date = new Date(dateStr + "Z");
      }
    }

    if (isNaN(date.getTime())) return "—";

    const userTz = timezone || getUserTimezone();

    const formatOptions = {
      date: {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        timeZone: userTz,
      },
      datetime: {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: userTz,
        hour12: false,
      },
      time: {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: userTz,
        hour12: false, // 24-hour format, no seconds
      },
      full: {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: userTz,
        hour12: false, // 24-hour format, no seconds
      },
      "date-short": {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: userTz,
      },
      "date-long": {
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: userTz,
      },
    };

    // For datetime format, use native JS to format as dd/MM/yyyy - HH:mm
    if (format === "datetime") {
      try {
        // Convert UTC date to user's timezone using Intl.DateTimeFormat
        const formatter = new Intl.DateTimeFormat("en-GB", {
          timeZone: userTz,
          year: "2-digit",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });

        const parts = formatter.formatToParts(date);
        const day = parts.find((p) => p.type === "day").value;
        const month = parts.find((p) => p.type === "month").value;
        const year = parts.find((p) => p.type === "year").value;
        const hours = parts.find((p) => p.type === "hour").value;
        const minutes = parts.find((p) => p.type === "minute").value;

        return `${day}/${month}/${year} - ${hours}:${minutes}`;
      } catch (e) {
        // Fallback: manually format if Intl fails
        try {
          const dateStr = date.toLocaleString("en-GB", {
            timeZone: userTz,
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });

          // Parse "DD/MM/YYYY, HH:MM" format
          const parts = dateStr.split(", ");
          if (parts.length === 2) {
            return `${parts[0]} - ${parts[1]}`;
          }
        } catch (e2) {
          // Final fallback
        }

        // Last resort: create new date in timezone
        const d = new Date(date.toLocaleString("en-US", { timeZone: userTz }));
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = String(d.getFullYear());
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${day}/${month}/${year} - ${hours}:${minutes}`;
      }
    }

    // For date format, always use 'en-GB' to ensure dd/mm/yyyy
    if (format === "date") {
      return date.toLocaleDateString("en-GB", formatOptions.date);
    }

    // "26 June, 2026" — long month name with a comma before the year.
    if (format === "date-long") {
      try {
        const parts = new Intl.DateTimeFormat(
          "en-GB",
          formatOptions["date-long"]
        ).formatToParts(date);
        const day = parts.find((p) => p.type === "day")?.value;
        const month = parts.find((p) => p.type === "month")?.value;
        const year = parts.find((p) => p.type === "year")?.value;
        if (day && month && year) return `${day} ${month}, ${year}`;
      } catch (e) {
        // fall through to the generic locale formatting below
      }
      return date.toLocaleDateString("en-GB", formatOptions["date-long"]);
    }

    const options = formatOptions[format] || formatOptions.date;
    return date.toLocaleDateString("en-GB", options);
  } catch (e) {
    console.error("Error formatting UTC date:", e, utcDate);
    return "—";
  }
}

/**
 * Format date for CSV/Excel export (always use date part only, no timezone conversion needed for export)
 * @param {string|Date} date - Date string or Date object
 * @returns {string} Date in YYYY-MM-DD format
 */
export function formatDateForExport(date) {
  if (!date) return "";
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch (e) {
    return "";
  }
}

/**
 * Format date for display in table (compact format)
 * @param {string|Date} utcDate - UTC date string or Date object
 * @returns {string} Formatted date string
 */
export function formatDateCompact(utcDate) {
  return formatUTCDate(utcDate, "date");
}

/**
 * Format date and time for display
 * @param {string|Date} utcDate - UTC date string or Date object
 * @returns {string} Formatted date and time string
 */
export function formatDateTime(utcDate) {
  return formatUTCDate(utcDate, "datetime");
}

/**
 * Get current UTC timestamp in ISO format
 * @returns {string} ISO 8601 UTC timestamp
 */
export function getCurrentUTC() {
  return new Date().toISOString();
}
